import { effect, inject, Injector, Signal, signal, untracked } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import {
  DialogMutationMessages,
  DialogMutationResult,
  DialogMutationService,
} from './dialog-mutation-service';

export interface DialogRunOptions<T> extends DialogMutationMessages {
  /**
   * What to hand `MatDialogRef.close()` on success. Defaults to `true`; a dialog whose caller
   * needs the created entity (the duplicated ŠVP's id, say) supplies it from the response.
   */
  closeWith?: (value: T) => unknown;
  /**
   * A success that still leaves work in the form — a message some recipients did not get — keeps
   * the dialog open, so the user can act on what the response said.
   */
  stayOpenWhen?: (value: T) => boolean;
}

export interface DialogSubmissionOptions {
  /**
   * Reactive read of the form's model. A change to it clears a failure message left over from the
   * previous attempt, so the user who fixes a duplicate code does not keep reading why the last
   * save failed. Omit for a dialog with nothing to edit.
   */
  clearErrorOn?: () => unknown;
}

export interface DialogSubmission {
  /** Whether a save is in flight — disables the form's buttons. */
  readonly isSaving: Signal<boolean>;
  /** Why the last save failed, or `null`. Rendered inline; never a snack bar. */
  readonly errorMessage: Signal<string | null>;
  /**
   * Puts a message in the dialog's error slot without running anything — for a check the form
   * itself makes, such as two password fields that do not match.
   */
  setError(message: string): void;
  /** Empties the error slot — for a dialog that has moved the message somewhere more specific. */
  clearError(): void;
  /**
   * Runs the mutation, then closes the dialog unless it failed in a way worth retrying — in which
   * case the form stays on screen with `errorMessage` set. The result is returned for the rare
   * dialog that has to override what happens next by the API's code (see `LicenseDialog`'s
   * `LICENSE_PERIOD_OVERLAP`).
   */
  run<T>(
    source: Observable<T>,
    context: string,
    options?: DialogRunOptions<T>,
  ): Promise<DialogMutationResult<T>>;
}

/**
 * The submit half of a dialog that performs its own action: the in-flight flag, the inline failure
 * message, and the close-or-stay-open decision, which is otherwise the same twenty lines in every
 * dialog. Call it in a field initializer, like `inject()`.
 */
export function dialogSubmission(options: DialogSubmissionOptions = {}): DialogSubmission {
  const dialogRef = inject(MatDialogRef<unknown>);
  const dialogMutation = inject(DialogMutationService);
  const injector = inject(Injector);

  const isSaving = signal(false);
  const errorMessage = signal<string | null>(null);

  // An escape or a backdrop click mid-request would leave the write running with nowhere to report.
  effect(() => (dialogRef.disableClose = isSaving()), { injector });

  if (options.clearErrorOn) {
    const read = options.clearErrorOn;
    effect(
      () => {
        read();
        untracked(() => errorMessage.set(null));
      },
      { injector },
    );
  }

  return {
    isSaving: isSaving.asReadonly(),
    errorMessage: errorMessage.asReadonly(),
    setError: (message: string) => errorMessage.set(message),
    clearError: () => errorMessage.set(null),
    async run<T>(source: Observable<T>, context: string, runOptions: DialogRunOptions<T> = {}) {
      if (isSaving()) {
        return { outcome: 'aborted' } as DialogMutationResult<T>;
      }
      isSaving.set(true);
      errorMessage.set(null);

      const { closeWith, stayOpenWhen, ...messages } = runOptions;
      const result = await dialogMutation.run<T>(source, context, messages);
      isSaving.set(false);

      if (result.outcome === 'failed') {
        errorMessage.set(result.message);
      } else if (result.outcome === 'success') {
        if (stayOpenWhen?.(result.value)) return result;
        dialogRef.close(closeWith ? closeWith(result.value) : true);
      } else if (result.outcome === 'committed') {
        dialogRef.close(true);
      } else {
        dialogRef.close(false);
      }
      return result;
    },
  };
}
